import {AuthUser} from '@/features/auth/types'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'
import {AccountModalHeader, AccountModalSection} from '@/features/account/components/account-atoms/AccountAtoms'
import {ModalXCloseButton} from '@/components/ui/modal-atoms/ModalAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {useAccountStore} from '@/features/account/store/useAccountStore'
import {AccountModalProfileRole} from '@/features/account/components/account-modal-profile-role/AccountModalProfileRole'
import {ChangeEmailForm} from '@/features/account/components/change-email-form/ChangeEmailForm'
import {AccountEditProfileHeader} from '@/features/account/components/account-edit-profile-header/AccountEditProfileHeader'
import {StyledEditProfileTitle} from './style'
import {AccountEmailPendingVerification} from '@/features/account/components/account-email-pending-verification/AccountEmailPendingVerification'
import {ChangePasswordForm} from '@/features/account/components/change-password-form/ChangePasswordForm'
import {Text} from '@/components/ui/text/Text'

export const AccountModalProfile = ({user, onClose}: {user: AuthUser; onClose: () => void}) => {
    const {t} = useTranslation()
    const editingSection = useAccountStore(state => state.editingSection)
    const userFullName = useMemo(() => `${user?.firstName} ${user?.lastName}`, [user])

    return (
        <>
            <AccountModalHeader>
                <h3>{t('commons:profile')}</h3>
                <ModalXCloseButton onClick={onClose} btnSize={'xs'} />
            </AccountModalHeader>

            <AccountModalSection title={t('commons:fullname')}>
                <Flexbox gap={2.5} align="center">
                    <Avatar name={userFullName} imageUrl={user?.avatarUrl ?? '-'} size={'lg'} />
                    <Flexbox direction="column">
                        <Text size="textLg" weight="500">
                            {userFullName}
                        </Text>
                        {user.roleId && <AccountModalProfileRole roleId={user.roleId} />}
                    </Flexbox>
                </Flexbox>
            </AccountModalSection>

            <AccountModalSection title={t('commons:email_address')}>
                {editingSection == 'email' ? (
                    <Flexbox direction="column" gap={3}>
                        <AccountEditProfileHeader
                            field="email"
                            title={<StyledEditProfileTitle>{t('account:change_email')}</StyledEditProfileTitle>}
                        />
                        <ChangeEmailForm userId={user.id} />
                    </Flexbox>
                ) : user.newEmailToVerify ? (
                    <AccountEmailPendingVerification
                        currentEmail={user.email}
                        newEmail={user.newEmailToVerify}
                        userId={user.id}
                    />
                ) : (
                    <AccountEditProfileHeader field="email" title={<p>{user.email}</p>} />
                )}
            </AccountModalSection>

            <AccountModalSection title={t('commons:password')}>
                {editingSection == 'password' ? (
                    <Flexbox direction="column" gap={4}>
                        <AccountEditProfileHeader
                            field="password"
                            title={<StyledEditProfileTitle>{t('account:change_password')}</StyledEditProfileTitle>}
                        />
                        <ChangePasswordForm />
                    </Flexbox>
                ) : (
                    <AccountEditProfileHeader field="password" title={<p>•••••••••••</p>} />
                )}
            </AccountModalSection>
        </>
    )
}
